// src/components/CatylexSearch/CatylexIcons.tsx
// Shared SVG icon components for CatylexSearch feature

import * as React from "react";
import { catylexColors } from "./catylexSearchStyles";

/**
 * Every icon here is decorative — each one sits beside text that already says
 * what it means — so all of them are hidden from assistive tech and kept out of
 * the tab order rather than announced as unlabelled graphics.
 */

interface SearchIconProps {
  stroke: string;
}

export const SearchIcon: React.FC<SearchIconProps> = ({ stroke }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

interface ErrorIconProps {
  stroke: string;
}

export const ErrorIcon: React.FC<ErrorIconProps> = ({ stroke }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0, marginTop: "1px" }}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

interface ChevronIconProps {
  isExpanded: boolean;
}

export const ChevronIcon: React.FC<ChevronIconProps> = ({ isExpanded }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={catylexColors.mutedForeground}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{
      width: "14px",
      height: "14px",
      flexShrink: 0,
      transition: "transform 0.2s ease",
      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
    }}
  >
    <path d="M9 18L15 12L9 6" />
  </svg>
);

interface DocumentIconProps {
  size?: number;
  stroke?: string;
}

/** Page-with-lines glyph — marks a loaded contract, as opposed to a search hit. */
export const DocumentIcon: React.FC<DocumentIconProps> = ({
  size = 14,
  stroke = catylexColors.primary,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0 }}
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

interface DownloadIconProps {
  size?: number;
  stroke?: string;
}

/** Arrow-into-tray glyph — shown while a contract is being pulled from Catylex. */
export const DownloadIcon: React.FC<DownloadIconProps> = ({
  size = 20,
  stroke = catylexColors.primary,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0 }}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const ExternalLinkIcon: React.FC = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11M15 3H21M21 3V9M21 3L10 14"
      stroke={catylexColors.primary}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
